import _ from "lodash";
import { RootState } from "src/store";

import { Group, License } from "@dashboard/devices/types";
import { selectGroupsEntities } from "@dashboard/groups/store/selectors";
import { selectLicensesEntities } from "@dashboard/licenses/store/selectors";
import { createSelector } from "@reduxjs/toolkit";

type EntityTypes = "subscription" | "group" | "license" | "device";
export type BatchActionState = {
    isActive: boolean;
    type: "move" | null;
    entity: EntityTypes | null;
    selection: number[];
    targetId: number | null;
};
export const selectBatchActionIsActive = (state: RootState): boolean => state.devices.batchAction.isActive;
export const selectBatchActionEntity = (state: RootState): EntityTypes | null => state.devices.batchAction.entity;
export const selectBatchActionType = (state: RootState): BatchActionState["type"] => state.devices.batchAction.type;
const selectBatchActionSelection = (state: RootState): number[] => state.devices.batchAction?.selection ?? [];
export const selectBatchActionSelectionTransformed = createSelector([selectBatchActionSelection], (selected) => {
    return _.mapValues(_.keyBy(selected), () => true);
});
export const selectBatchActionTargetId = (state: RootState): number | null => state.devices.batchAction?.targetId;

const selectBatchActionIsEntityLicense = createSelector(selectBatchActionEntity, (entity) => entity === "license");
const selectBatchActionIsEntityDevice = createSelector(selectBatchActionEntity, (entity) => entity === "device");
const selectBatchActionIsEntityGroup = createSelector(selectBatchActionEntity, (entity) => entity === "group");
export const selectBatchActionIsTypeMove = createSelector(selectBatchActionType, (type) => type === "move");
export const selectBatchActionIsActiveLicense = createSelector(
    selectBatchActionIsActive,
    selectBatchActionIsEntityLicense,
    (isActive, isLicense) => isActive && isLicense,
);
export const selectBatchActionIsActiveDevice = createSelector(
    selectBatchActionIsActive,
    selectBatchActionIsEntityDevice,
    (isActive, isDevice) => isActive && isDevice,
);
const selectBatchActionIsActiveGroup = createSelector(
    selectBatchActionIsActive,
    selectBatchActionIsEntityGroup,
    (isActive, isGroup) => isActive && isGroup,
);
// Takes the entity maps rather than the whole state: an input selector of `(state) => state` changes identity on
// every dispatched action, so the memo never held and this re-mapped the entire selection each time.
export const selectBatchActionSelectionEntities = createSelector(
    selectBatchActionSelection,
    selectBatchActionIsActiveGroup,
    selectBatchActionIsActiveLicense,
    selectGroupsEntities,
    selectLicensesEntities,
    (selection, isGroup, isLicense, groups, licenses) => {
        return selection
            .map((id) => {
                if (isGroup) {
                    return groups[id];
                } else if (isLicense) {
                    return licenses[id];
                }
                return null;
            })
            .filter((item) => !!item) as License[] | Group[];
    },
);
